import * as THREE from 'three';
import { OrbitControls, FontLoader } from 'three/examples/jsm/Addons.js';
import GeoPoint from './GeoPoint';
import Line3D from './Line3D';
import { type Point, type Line } from './../types';
import roboto from "./../roboto.json";

type InitializedGeometry = Geometry & {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  font: any;
  controls: OrbitControls;
  origin: GeoPoint;
};

export default class Geometry {
  container: HTMLElement;
  scene: THREE.Scene | null = null;
  camera: THREE.PerspectiveCamera | null = null;
  renderer: THREE.WebGLRenderer | null = null;
  font: any;
  controls: OrbitControls | null = null;

  origin: GeoPoint | null = null;
  points: GeoPoint[] = [];
  lines: Line3D[] = [];

  maxDistance: number = 1_000;
  disposed: boolean = false;

  constructor (container: HTMLElement) {
    this.container = container;
  }

  async init () {
    this.disposed = false;
    this.scene = new THREE.Scene();
    // Setting up the canvas and three
    this.scene.background = new THREE.Color( 0xf0f0f0 );

    // Camera
    this.camera = new THREE.PerspectiveCamera( 75, this.container.clientWidth / this.container.clientHeight, 0.1, this.maxDistance );
    //
    // Renderer
    this.renderer = new THREE.WebGLRenderer();
    this.renderer.setSize( this.container.clientWidth, this.container.clientHeight );
    this.renderer.shadowMap.enabled = true;
    this.container.appendChild( this.renderer.domElement );

    // Font
    this.font = await this.getFont();
    if (this.disposed || !this.scene || !this.camera || !this.renderer) {
      return this;
    }

    this.controls = new OrbitControls( this.camera, this.renderer.domElement );
    this.camera.position.set( 0, 10, 5 );
    this.controls.update();

    this.origin = new GeoPoint(new THREE.Vector3(0,0,0), this.scene, this.camera, this.font, 'Origin (0;0;0)', "origin");
    this.isInitialized();
    return this;
  }

  isInitialized(): this is InitializedGeometry {
    const missingProperties = [
      ["scene", this.scene],
      ["camera", this.camera],
      ["renderer", this.renderer],
      ["font", this.font],
      ["controls", this.controls],
      ["origin", this.origin]
    ].filter(([, value]) => value === null || value === undefined).map(([property]) => property);

    if (this.disposed) {
      console.warn("Geometry - instance is disposed. Call init() before rendering");
      return false;
    }

    if (missingProperties.length > 0) {
      console.warn(`Geometry - missing initialized properties: ${missingProperties.join(", ")}. Call init() before rendering`);
      return false;
    }

    return true;
  }

  async getFont() {
    const loader = new FontLoader();
    return loader.parse(roboto);
  }

  addLight() {
    if (!this.isInitialized()) return;

    // Adding light
    this.scene.add( new THREE.AmbientLight( 0xf0f0f0, 3 ) );
    const light = new THREE.SpotLight( 0xffffff, 4.5 );
    light.position.set( 0, 1500, 200 );
    light.angle = Math.PI * 0.2;
    light.decay = 0;
    light.castShadow = true;
    light.shadow.camera.near = 200;
    light.shadow.camera.far = 2000;
    light.shadow.bias = - 0.000222;
    light.shadow.mapSize.width = 1024;
    light.shadow.mapSize.height = 1024;
    this.scene.add( light );
  }

  addGridHelpers() {
    if (!this.isInitialized()) return;

    // Grid helper
    const helper = new THREE.GridHelper( 100, 100 );
    helper.position.y = 0;
    helper.material.opacity = 0.15;
    helper.material.transparent = true;
    this.scene.add( helper );
  }

  render() {
    if (!this.isInitialized()) return;

    // Add light to scene
    this.addLight();

    // Add grid helpers
    this.addGridHelpers();

    // Controls
    this.origin.render();
    this.points.forEach(p => p.render());
    this.lines.forEach(l => l.render());
    this.renderer.setAnimationLoop( this.animate.bind(this) );
  }

  animate(time: DOMHighResTimeStamp) {
    if (!this.isInitialized()) return;

    this.controls.update();

    this.origin.update();
    this.points.forEach(p => p.update());
    this.renderer.render( this.scene, this.camera );
  }

  addPoint(point: Point) {
    if (!this.isInitialized()) return;

    this.points.push(
      new GeoPoint(
        new THREE.Vector3(point.x, point.y, point.z),
        this.scene,
        this.camera,
        this.font,
        point.name,
        // Pretty bad IMO
        point.id
      )
    );
  }

  removePoint(id: string) {
    const pointIndex = this.points.findIndex(gp => gp.id === id);
    if (pointIndex < 0) {
      console.warn(`Geometry - GeoPoint with id: ${id} doesn't exist.`);
      return this;
    }

    const pointToRemove = this.points.splice(pointIndex, 1)[0];
    if (pointToRemove) {
      pointToRemove.dispose();
    }
    return this;
  }

  addLine(line: Line) {
    if (!this.isInitialized()) return;

    this.lines.push(
      new Line3D(
        {
          a: new THREE.Vector3(line.pointA.x, line.pointA.y, line.pointA.z),
          b: new THREE.Vector3(line.pointB.x, line.pointB.y, line.pointB.z)
        },
        this.scene,
        this.maxDistance,
        line.id
      )
    );
  }

  removeLine(id: string) {
    const lineIndex = this.lines.findIndex(d => d.id === id);
    if (lineIndex < 0) {
      console.warn(`Geometry - Line3D with id ${id} doesn't exist.`);
      return;
    }

    const lineToRemove = this.lines.splice(lineIndex, 1)[0];

    if (lineToRemove) {
      lineToRemove.dispose();
    }
  }

  renderPoints() {
    this.points.forEach(p => p.dispose(false).render());
  }

  renderLines() {
    this.lines.forEach(d => d.dispose(false).render());
  }

  getLine(id: string): Line3D | undefined {
    return this.lines.find(d => d.id === id);
  }

  goToPoint(p: THREE.Vector3) {
    if (!this.isInitialized()) return;

    this.camera.position.set(p.x, p.y + 5, p.z + 5);
    this.controls.target = p;
    this.camera.updateProjectionMatrix();
    this.controls.update();
    return this;
  }

  dispose() {
    this.disposed = true;

    this.renderer?.setAnimationLoop(null);

    this.origin?.dispose();
    this.points.forEach(p => p.dispose());
    this.lines.forEach(l => l.dispose());

    this.controls?.dispose();

    if (this.renderer) {
      const canvas = this.renderer.domElement;
      this.renderer.dispose();
      canvas.parentElement?.removeChild(canvas);
    }

    this.origin = null;
    this.points = [];
    this.lines = [];
    this.controls = null;
    this.renderer = null;
    this.camera = null;
    this.scene = null;
  }
}
